/* eslint-disable import/prefer-default-export */
import axios, { AxiosResponse } from 'axios';
import { DistrictReportData } from '@CT-Types/districtReport';

/**
 * Response from GET /api/districts/
 * Returns the current admin user's districts
 */
export interface UserDistrictsResponse {
  districts: Array<{
    id: number;
    name: string;
  }>;
}

/**
 * Get the current admin user's districts
 * Only accessible to district admins
 * GET /api/districts/
 */
export const getUserDistricts = async (): Promise<UserDistrictsResponse> => {
  const url = '/api/districts/';
  const response: AxiosResponse = await axios.get(url);
  return response.data;
};

export interface RequestDistrictReportParams {
  reportType: 'skill' | 'standard' | 'lesson';
  districtId: number;
  examId?: number | null;
  curriculumsubjectIds?: string[];
}

export interface RequestDistrictReportResponse {
  data: DistrictReportData;
}

/**
 * Request generation of a district report
 * POST /api/learnosityexams/district-reports/{reportType}/
 */
export const requestDistrictReport = async (
  params: RequestDistrictReportParams,
): Promise<RequestDistrictReportResponse> => {
  const { reportType, districtId, examId, curriculumsubjectIds = [] } = params;

  const url = `/api/learnosityexams/district-reports/${reportType}/`;

  const body = {
    district_id: districtId,
    exam_id: examId || null,
    curriculumsubject_ids: curriculumsubjectIds,
  };

  const response: AxiosResponse = await axios.post(url, body);
  return response.data;
};

export interface CsvExportQueuedResponse {
  status: 'queued';
  report_name: string;
}

/**
 * Queue a CSV export of a district report. The file is generated in the
 * background; the download URL arrives on the EXAM_REPORT_CSV_COMPLETED
 * websocket event.
 * POST /api/learnosityexams/district-reports/{reportType}/export-csv/
 */
export const requestDistrictReportCsv = async (
  params: RequestDistrictReportParams,
): Promise<CsvExportQueuedResponse> => {
  const { reportType, districtId, examId, curriculumsubjectIds = [] } = params;

  const url = `/api/learnosityexams/district-reports/${reportType}/export-csv/`;

  const body = {
    district_id: districtId,
    exam_id: examId || null,
    curriculumsubject_ids: curriculumsubjectIds,
  };

  const response: AxiosResponse = await axios.post(url, body);
  return response.data;
};

export interface GroupReportDataParams {
  districtId: number;
  curriculumSubjectId: number;
}

export interface GroupReportDataResponse {
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'failed';
  dataset_id: string;
  name_map?: Record<string, string>;
  user_count?: number;
  message?: string;
  error?: string;
  completed_at?: string;
}

/**
 * Get the dataset_id and name_map for Learnosity Group Reports
 * GET /api/learnosityexams/districts/{districtId}/curriculum-subjects/{curriculumSubjectId}/dataset/
 *
 * Returns dataset info if available, or triggers generation if not.
 * Frontend should poll this endpoint until status is 'completed' or 'failed'.
 */
export const getGroupReportData = async (
  params: GroupReportDataParams,
): Promise<GroupReportDataResponse> => {
  const { districtId, curriculumSubjectId } = params;
  const url = `/api/learnosityexams/districts/${districtId}/curriculum-subjects/${curriculumSubjectId}/dataset/`;
  const response: AxiosResponse = await axios.get(url);
  return response.data;
};

/**
 * Force refresh/regenerate a Learnosity Group Report dataset
 * POST /api/learnosityexams/districts/{districtId}/curriculum-subjects/{curriculumSubjectId}/dataset/refresh/
 */
export const refreshGroupReportData = async (
  params: GroupReportDataParams,
): Promise<GroupReportDataResponse> => {
  const { districtId, curriculumSubjectId } = params;
  const url = `/api/learnosityexams/districts/${districtId}/curriculum-subjects/${curriculumSubjectId}/dataset/refresh/`;
  const response: AxiosResponse = await axios.post(url);
  return response.data;
};

/**
 * Helper to check if dataset is ready
 */
export const isDatasetReady = (response: GroupReportDataResponse): boolean => {
  return response.status === 'completed' && !!response.dataset_id && !!response.name_map;
};

/**
 * Helper to check if dataset is still generating
 */
export const isDatasetGenerating = (response: GroupReportDataResponse): boolean => {
  return ['pending', 'uploading', 'processing'].includes(response.status);
};
